import { useEffect, useRef } from 'react';
import { rmsLevel } from '@/services/voice/live';
import { abrirMicrofone } from '@/services/voice/mics';
import { getWakeEngine } from '@/services/voice/wake/store';

/** Barras do gráfico: a mais à direita é o agora, as outras o passado recente. */
const BARRAS = 8;
/** Uma coluna nova a cada ~70 ms: 8 barras ≈ meio segundo de histórico. */
const PASSO_MS = 70;

/** Quadro do `MediaStreamTrackProcessor` (o `AudioData` do WebCodecs). */
interface AudioFrame {
  numberOfFrames: number;
  copyTo(dest: Float32Array, opts: { planeIndex: number; format?: string }): void;
  close(): void;
}
type TrackProcessorCtor = new (init: { track: MediaStreamTrack }) => {
  readable: { getReader(): ReadableStreamDefaultReader<AudioFrame> };
};

/**
 * RMS → altura 0..1 em escala de decibéis: −60 dB (sala quieta) fica no
 * chão, −12 dB (voz perto do microfone) enche a barra. Linear, a fala
 * normal mal mexeria.
 */
export function alturaDoNivel(rms: number): number {
  if (!(rms > 0)) return 0;
  const db = 20 * Math.log10(rms);
  return Math.min(1, Math.max(0, (db + 60) / 48));
}

/**
 * Gráfico de nível do microfone ao lado do ouvido do "Jarvis" (spec 011):
 * mostra que o microfone está vivo e captando enquanto a escuta está ligada.
 *
 * Abre uma captura PRÓPRIA do microfone que a escuta ouve (o padrão, ou a
 * mistura da spec 016 com o motor próprio) — o reconhecedor do Chrome
 * não expõe o áudio — e só com `ativo` (a escuta já está com o mic aberto;
 * nada sai do computador, é só medição). Sem processamento (eco, ruído,
 * ganho automático) pra mostrar a sensibilidade real, não a "embelezada".
 * Desenha direto no DOM, sem re-render do React a cada quadro.
 */
export const MicLevelGraph = ({ ativo }: { ativo: boolean }) => {
  const barrasRef = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    if (!ativo || !navigator.mediaDevices?.getUserMedia) return;
    const els = barrasRef.current;
    let parado = false;
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let reader: ReadableStreamDefaultReader<AudioFrame> | null = null;
    let raf = 0;
    let timer: ReturnType<typeof setInterval> | undefined;
    const historico = new Array<number>(BARRAS).fill(0);
    // Maior RMS visto desde a última coluna: pico, não média — sílaba curta aparece.
    let pico = 0;

    const desenhar = () => {
      historico.shift();
      historico.push(alturaDoNivel(pico));
      pico = 0;
      historico.forEach((h, i) => {
        const el = els[i];
        if (el) el.style.transform = `scaleY(${Math.max(0.12, h)})`;
      });
    };

    const lerPelaTrack = (track: MediaStreamTrack): boolean => {
      const Processor = (globalThis as { MediaStreamTrackProcessor?: TrackProcessorCtor }).MediaStreamTrackProcessor;
      if (!Processor) return false;
      try {
        reader = new Processor({ track }).readable.getReader();
      } catch {
        return false;
      }
      const r = reader;
      void (async () => {
        for (;;) {
          let frame: AudioFrame;
          try {
            const res = await r.read();
            if (res.done) return;
            frame = res.value;
          } catch {
            return;
          }
          try {
            const amostras = new Float32Array(frame.numberOfFrames);
            frame.copyTo(amostras, { planeIndex: 0, format: 'f32-planar' });
            pico = Math.max(pico, rmsLevel(amostras));
          } catch {
            /* quadro ilegível: pula */
          } finally {
            frame.close();
          }
        }
      })();
      return true;
    };

    void (async () => {
      try {
        // Mostra o que a escuta ouve: a mistura só no motor próprio (o
        // reconhecedor do Chrome sempre ouve o microfone padrão).
        const s = await abrirMicrofone(
          { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          { mistura: getWakeEngine() === 'proprio', uso: 'escuta' },
        );
        if (parado) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        const track = s.getAudioTracks()[0];
        const c = new AudioContext();
        ctx = c;
        // Sem gesto na página o Chrome pode criar o contexto suspenso (o
        // analisador ficaria mudo e o gráfico mentiria "mic morto"): lê a
        // track direto, como o ao vivo faz (`liveSession.startTrackReader`).
        if (c.state === 'suspended' && track && lerPelaTrack(track)) {
          c.close().catch(() => {});
          ctx = null;
        } else {
          const analyser = c.createAnalyser();
          analyser.fftSize = 1024;
          c.createMediaStreamSource(s).connect(analyser);
          const buf = new Float32Array(analyser.fftSize);
          const ler = () => {
            if (parado) return;
            analyser.getFloatTimeDomainData(buf);
            pico = Math.max(pico, rmsLevel(buf));
            raf = requestAnimationFrame(ler);
          };
          ler();
        }
        timer = setInterval(desenhar, PASSO_MS);
      } catch {
        // Sem acesso ao mic aqui: o gráfico fica no chão. Quem avisa erro de
        // microfone é a escuta (chip) e o ditado — não duplicar.
      }
    })();

    return () => {
      parado = true;
      cancelAnimationFrame(raf);
      clearInterval(timer);
      reader?.cancel().catch(() => {});
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close().catch(() => {});
      els.forEach((el) => {
        if (el) el.style.transform = 'scaleY(0.12)';
      });
    };
  }, [ativo]);

  if (!ativo) return null;
  return (
    <span className="flex h-4 shrink-0 items-center gap-px" aria-hidden data-testid="mic-level-graph">
      {Array.from({ length: BARRAS }, (_, i) => (
        <span
          key={i}
          ref={(el) => {
            barrasRef.current[i] = el;
          }}
          className="h-full w-[2px] origin-center rounded-full bg-current transition-transform duration-75"
          style={{ transform: 'scaleY(0.12)', opacity: 0.45 + (0.55 * (i + 1)) / BARRAS }}
        />
      ))}
    </span>
  );
};
